/**
 * Centralized authentication / session helpers.
 * Used by the Axios response interceptor and Vue Router navigation guards.
 *
 * Token key used by this app: localStorage "access_token"
 * (also accepts "access_Token" / "token" for compatibility).
 */

import { notifyNativeLogout } from '@/utils/nativeBridge';
import {
    captureRememberMeCredentials,
    restoreRememberMeCredentials,
} from '@/helpers/rememberMeCredentials';

/** Prevents concurrent API 401/403 failures from triggering logout many times. */
let isLoggingOut = false;

/** Avoid attaching the same auth interceptors more than once on an Axios instance. */
const attachedInstances = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
const attachedFallback = [];

const AUTH_MESSAGE_PATTERNS = [
    'token expired',
    'invalid token',
    'jwt expired',
    'unauthorized',
    'unauthorised',
    'authentication failed',
    'not authenticated',
    'session expired',
    'bearer token',
    'token is invalid',
    'token has expired',
    'security token',
];

const AUTH_RELATED_LOCAL_STORAGE_KEYS = [
    'access_token',
    'access_Token',
    'token',
    'refresh_token',
    'refreshToken',
    'userinfo',
    'userId',
    'companyId',
    'userType',
    'userTypeId',
    'moduleList',
    'usersucess',
    'connectionId',
];

/** Routes that do not require an access token (login, password reset, public embeds). */
const PUBLIC_PATH_PREFIXES = [
    '/login',
    '/logincompanyuser',
    '/mobiledashboard',
    '/account/forgot-password',
    '/account/resetpassword',
    '/account/setpassword',
    '/account/appstore',
    '/application',
    '/videochat',
    '/uploadfiles',
    '/dashboardlanding',
    '/s/', // short links (friend expense SMS) → redirect via API
];

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please login again.';

export function getAccessToken() {
    return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('access_Token') ||
        localStorage.getItem('token') ||
        null
    );
}

export function hasAccessToken() {
    const token = getAccessToken();
    return token != null && String(token).trim() !== '';
}

/**
 * Decode JWT payload (no signature verification — expiry check only).
 * Returns null when the token is missing or not a JWT.
 */
function parseJwtPayload(token) {
    try {
        const parts = String(token || '').split('.');
        if (parts.length < 2) {
            return null;
        }
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const json = decodeURIComponent(
            atob(padded)
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

/**
 * Returns JWT exp in milliseconds, or null if the token has no exp claim.
 */
export function getAccessTokenExpiryMs(token = getAccessToken()) {
    const payload = parseJwtPayload(token);
    if (!payload || payload.exp == null || Number.isNaN(Number(payload.exp))) {
        return null;
    }
    return Number(payload.exp) * 1000;
}

/**
 * Existing session validation used by the router and menu navigation.
 * Valid when an access token is present and (when JWT) not past its exp claim.
 */
export function isSessionValid() {
    const token = getAccessToken();
    if (token == null || String(token).trim() === '') {
        return false;
    }

    const expiryMs = getAccessTokenExpiryMs(token);
    // Non-JWT / opaque tokens: presence is enough; Axios interceptor still enforces backend auth.
    if (expiryMs == null) {
        return true;
    }

    // Small skew so clock drift does not cause immediate false expiry.
    return Date.now() < expiryMs - 5000;
}

/**
 * Central pre-navigation check. Reuses forceLogoutDueToAuthFailure.
 * @returns {boolean} true when navigation may continue
 */
export function ensureValidSessionOrLogout(options = {}) {
    if (isSessionValid()) {
        return true;
    }

    const hadToken = hasAccessToken();
    forceLogoutDueToAuthFailure({
        ...options,
        // Toast only when a token existed but is no longer valid (expired/invalid).
        showToast: hadToken ? options.showToast : null,
        message: options.message || SESSION_EXPIRED_MESSAGE
    });
    return false;
}

export function isLoginPath(path) {
    const p = normalizePath(path);
    return p === '/login' || p.startsWith('/login/') || p.startsWith('/logincompanyuser');
}

export function isPublicPath(path) {
    const p = normalizePath(path);
    if (!p || p === '/') {
        return true;
    }
    return PUBLIC_PATH_PREFIXES.some(
        (prefix) => p === prefix || p.startsWith(`${prefix}/`) || p.startsWith(prefix)
    );
}

function normalizePath(path) {
    if (path == null) {
        return '';
    }
    let value = String(path).split('?')[0].split('#')[0].trim().toLowerCase();
    if (!value.startsWith('/')) {
        value = `/${value}`;
    }
    if (value.length > 1 && value.endsWith('/')) {
        value = value.slice(0, -1);
    }
    return value;
}

function extractErrorText(errorOrResponse) {
    if (!errorOrResponse) {
        return '';
    }

    const parts = [];
    const response = errorOrResponse.response || errorOrResponse;
    const data = response?.data ?? errorOrResponse.data ?? errorOrResponse;

    if (typeof data === 'string') {
        parts.push(data);
    } else if (data && typeof data === 'object') {
        [
            data.message,
            data.Message,
            data.responseMessage,
            data.ResponseMessage,
            data.title,
            data.Title,
            data.error,
            data.Error,
            data.error_description,
            data.statusText,
        ].forEach((v) => {
            if (v != null && String(v).trim() !== '') {
                parts.push(String(v));
            }
        });
        if (Array.isArray(data.errors)) {
            parts.push(data.errors.join(' '));
        }
    }

    if (errorOrResponse.message) {
        parts.push(String(errorOrResponse.message));
    }
    if (response?.statusText) {
        parts.push(String(response.statusText));
    }

    return parts.join(' ').toLowerCase();
}

function messageLooksLikeAuthFailure(text) {
    if (!text) {
        return false;
    }
    return AUTH_MESSAGE_PATTERNS.some((pattern) => text.includes(pattern));
}

/**
 * Detect auth failures from Axios errors (HTTP status + message) and from
 * successful HTTP responses that embed StatusCode 401 in the body.
 *
 * @param {object} errorOrResponse Axios error or response
 * @param {{ fromSuccessResponse?: boolean }} [options]
 *        When true (HTTP 2xx path), only trust embedded StatusCode === 401
 *        to avoid logging out on unrelated business messages that mention "unauthorized".
 */
export function isAuthenticationFailure(errorOrResponse, options = {}) {
    if (!errorOrResponse) {
        return false;
    }

    const fromSuccessResponse = options.fromSuccessResponse === true;
    const response = errorOrResponse.response || errorOrResponse;
    const status = response?.status;
    const data = response?.data ?? errorOrResponse.data;
    const text = extractErrorText(errorOrResponse);

    const bodyStatus =
        data?.StatusCode ?? data?.statusCode ?? data?.status ?? data?.Status ?? null;
    if (bodyStatus === 401 || bodyStatus === '401') {
        return true;
    }

    // Success responses: do not scan free-text (too many false positives).
    if (fromSuccessResponse) {
        return false;
    }

    if (status === 401) {
        return true;
    }

    // 403 Forbidden only when the payload clearly indicates an auth/token issue
    // (do not treat routine permission denials as session expiry).
    if (status === 403 && messageLooksLikeAuthFailure(text)) {
        return true;
    }

    if (messageLooksLikeAuthFailure(text)) {
        return true;
    }

    return false;
}

function clearAuthStorage() {
    // Preserve UI preference used across logouts (same as Sidebar/Header logout).
    const adminTheme = localStorage.getItem('adminTheme');
    // Remember-me credentials must survive logout so /login can prefill.
    const rememberMe = captureRememberMeCredentials();

    AUTH_RELATED_LOCAL_STORAGE_KEYS.forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });

    // Drop remaining session/auth leftovers without wiping unrelated browser data forever.
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch (e) {
        // ignore storage errors
    }

    if (adminTheme != null && adminTheme !== '') {
        localStorage.setItem('adminTheme', adminTheme);
    }
    restoreRememberMeCredentials(rememberMe);
}

function clearPiniaAuthState() {
    try {
        // Lazy require keeps this helper free of Pinia boot-order issues at module load.
        // eslint-disable-next-line global-require
        const userInfoModule = require('@/store/UserInfo');
        const store = userInfoModule.useloggedUserInfo();
        if (store && typeof store.$reset === 'function') {
            store.$reset();
        } else if (store) {
            store.isAuth = false;
            store.user = null;
        }
    } catch (e) {
        // Pinia may not be active yet during early boot
    }
}

/**
 * Clears auth data and redirects to Login once.
 * Safe to call from many parallel failing API requests (isLoggingOut guard).
 *
 * @param {{ router?: object, showToast?: Function, message?: string, skipRedirect?: boolean }} options
 *        skipRedirect: when true (e.g. Vue Router beforeEach), caller redirects via next().
 */
export function forceLogoutDueToAuthFailure(options = {}) {
    const {
        router = null,
        showToast = null,
        message = SESSION_EXPIRED_MESSAGE,
        skipRedirect = false,
    } = options;

    if (isLoggingOut) {
        return;
    }
    isLoggingOut = true;

    try {
        // Notify native shell before clearing session (no-op on web).
        notifyNativeLogout();
        clearAuthStorage();
        clearPiniaAuthState();

        if (typeof showToast === 'function') {
            try {
                showToast(message);
            } catch (e) {
                // toast must not block logout
            }
        }

        if (skipRedirect) {
            return;
        }

        const currentPath =
            (router && router.currentRoute && router.currentRoute.value
                ? router.currentRoute.value.fullPath
                : window.location.pathname) || '/';

        // Avoid redirect loops on login / other public pages (e.g. app store download).
        if (isLoginPath(currentPath) || isPublicPath(currentPath)) {
            return;
        }

        if (router && typeof router.replace === 'function') {
            router.replace({ path: '/login' }).catch(() => {
                window.location.href = '/login';
            });
        } else {
            window.location.href = '/login';
        }
    } finally {
        // Allow a future session (after re-login) to logout again if needed.
        // Keep the flag set briefly so simultaneous 401s still collapse into one logout.
        setTimeout(() => {
            isLoggingOut = false;
        }, 1500);
    }
}

export function resetLoggingOutFlag() {
    isLoggingOut = false;
}

/**
 * Attach a global Axios response interceptor for session expiry handling.
 * Does not modify individual API methods; all failures funnel through here.
 */
export function attachAuthResponseInterceptor(axiosInstance, options = {}) {
    if (!axiosInstance || !axiosInstance.interceptors) {
        return;
    }

    if (attachedInstances) {
        if (attachedInstances.has(axiosInstance)) {
            return;
        }
        attachedInstances.add(axiosInstance);
    } else if (attachedFallback.indexOf(axiosInstance) !== -1) {
        return;
    } else {
        attachedFallback.push(axiosInstance);
    }

    const { router = null, showToast = null } = options;

    axiosInstance.interceptors.response.use(
        (response) => {
            // Some APIs return HTTP 200 with an embedded StatusCode 401 body.
            if (isAuthenticationFailure(response, { fromSuccessResponse: true })) {
                forceLogoutDueToAuthFailure({ router, showToast });
                return Promise.reject(response);
            }
            return response;
        },
        (error) => {
            // Skip logout for login / token endpoints so bad credentials don't loop.
            const url = String(error?.config?.url || '').toLowerCase();
            const isAuthEndpoint =
                url.includes('account/login') ||
                url.includes('/auth/signin') ||
                url.includes('refreshtoken');

            if (!isAuthEndpoint && isAuthenticationFailure(error)) {
                forceLogoutDueToAuthFailure({ router, showToast });
            }
            return Promise.reject(error);
        }
    );
}
